import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import {
  FaBed,
  FaBath,
  FaMapLocationDot,
  FaRulerCombined,
  FaCircleInfo,
  FaHouse,
} from "react-icons/fa6";
import SaleHeaderImage from "../assets/images/sale-header.webp";
import NoImage from "../assets/images/no-image.webp";
import Meta from "../components/Meta";
import HeaderImage from "../components/HeaderImage";
import CoverImage from "../assets/images/cover.webp";
import ReactGA from "react-ga4";
import { useAnalytics } from "../utils/analyticsContext";
import useProperties from "../hooks/useProperties";

const SalePage = () => {
  const { t, i18n } = useTranslation();
  const { initialized } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLanguage = i18n.language;

  const [filterInputs, setFilterInputs] = useState({
    minPrice: searchParams.get("minPrice") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    bedrooms: searchParams.get("bedrooms") || "",
    bathrooms: searchParams.get("bathrooms") || "",
    location: searchParams.get("location") || "",
    meters: searchParams.get("meters") || "",
  });

  const filters = {
    minPrice: searchParams.get("minPrice") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    bedrooms: searchParams.get("bedrooms") || "",
    bathrooms: searchParams.get("bathrooms") || "",
    location: searchParams.get("location") || "",
    meters: searchParams.get("meters") || "",
  };

  const offset = searchParams.get("offset") || "";
  const [prevOffsets, setPrevOffsets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("salePrevOffsets") || "[]");
    } catch {
      return [];
    }
  });

  const skipDebounce = useRef(false);
  const firstRender = useRef(true);

  useEffect(() => {
    skipDebounce.current = true;
    setFilterInputs({
      minPrice: searchParams.get("minPrice") || "",
      maxPrice: searchParams.get("maxPrice") || "",
      bedrooms: searchParams.get("bedrooms") || "",
      bathrooms: searchParams.get("bathrooms") || "",
      location: searchParams.get("location") || "",
      meters: searchParams.get("meters") || "",
    });
  }, [searchParams.toString()]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (skipDebounce.current) {
      skipDebounce.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const params = new URLSearchParams();
      Object.entries(filterInputs).forEach(([key, val]) => {
        if (val && val !== "0") params.set(key, val); else params.delete(key);
      });
      setPrevOffsets([]);
      setSearchParams(params);
    }, 600);
    return () => clearTimeout(timer);
  }, [filterInputs]);

  useEffect(() => {
    localStorage.setItem("salePrevOffsets", JSON.stringify(prevOffsets));
  }, [prevOffsets]);

  const { data: houses, nextOffset, loading, error } = useProperties({
    purpose: "Shitje",
    filters: {
      minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
      maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
      bedrooms: filters.bedrooms ? Number(filters.bedrooms) : undefined,
      bathrooms: filters.bathrooms ? Number(filters.bathrooms) : undefined,
      location: filters.location || undefined,
      meters: filters.meters ? Number(filters.meters) : undefined,
    },
    offset,
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    skipDebounce.current = true;
    setFilterInputs({
      minPrice: "",
      maxPrice: "",
      bedrooms: "",
      bathrooms: "",
      location: "",
      meters: "",
    });
    setPrevOffsets([]);
    setSearchParams({});
  };

  const handleNext = () => {
    if (!nextOffset) return;
    setPrevOffsets((prev) => [...prev, offset]);
    const params = new URLSearchParams(searchParams);
    params.set("offset", nextOffset);
    skipDebounce.current = true;
    setSearchParams(params);
  };

  const handleBack = () => {
    if (!prevOffsets.length) return;
    const last = prevOffsets[prevOffsets.length - 1];
    setPrevOffsets((prev) => prev.slice(0, -1));
    const params = new URLSearchParams(searchParams);
    if (last) params.set("offset", last); else params.delete("offset");
    skipDebounce.current = true;
    setSearchParams(params);
  };

  const handlePropertyClick = (house) => {
    if (initialized) {
      ReactGA.send({
        hitType: "event",
        eventCategory: "Sale Property",
        eventAction: "click",
        eventLabel: house.fields["Title"],
      });
    }
  };

  return (
    <>
      <Meta
        title={t("salePage.meta.title")}
        subtitle={t("salePage.meta.subtitle")}
        description={t("salePage.meta.description")}
        keyword={t("salePage.meta.keyword")}
        image={CoverImage}
      />

      <HeaderImage
        image={SaleHeaderImage}
        title={t("salePage.header.title")}
        actualPage={t("salePage.header.subtitle")}
      />

      <div className="flex flex-col items-center justify-center w-full bg-primary">
        <div className="bg-primary container py-6 px-2 mx-auto w-full flex flex-col">
          <div className="w-full bg-white rounded-md mb-4 p-4 text-center">
            <p className="text-gray-800">{t("salePage.filters.description")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:flex gap-4">
            {/* Filter Section */}
            <div className="w-full md:w-1/3 bg-white h-fit rounded-md p-4 lg:sticky-filter">
              <h2 className="text-xl font-bold mb-4">{t("rentPage.filters.title")}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700">{t("rentPage.filters.minPrice")}</label>
                  <input
                    type="number"
                    name="minPrice"
                    value={filterInputs.minPrice}
                    onChange={handleFilterChange}
                    className="w-full mt-1 p-2 border border-gray-300"
                    placeholder={t("rentPage.filters.minPrice")}
                  />
                </div>
                <div>
                  <label className="block text-gray-700">{t("rentPage.filters.maxPrice")}</label>
                  <input
                    type="number"
                    name="maxPrice"
                    value={filterInputs.maxPrice}
                    onChange={handleFilterChange}
                    className="w-full mt-1 p-2 border border-gray-300"
                    placeholder={t("rentPage.filters.maxPrice")}
                  />
                </div>
                <div>
                  <label className="block text-gray-700">{t("rentPage.filters.bedrooms")}</label>
                  <select
                    name="bedrooms"
                    value={filterInputs.bedrooms}
                    onChange={handleFilterChange}
                    className="w-full mt-1 p-2 border border-gray-300"
                  >
                    <option value="">{t("rentPage.filters.all")}</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700">{t("rentPage.filters.bathrooms")}</label>
                  <select
                    name="bathrooms"
                    value={filterInputs.bathrooms}
                    onChange={handleFilterChange}
                    className="w-full mt-1 p-2 border border-gray-300"
                  >
                    <option value="">{t("rentPage.filters.all")}</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700">{t("rentPage.filters.location")}</label>
                  <input
                    type="text"
                    name="location"
                    value={filterInputs.location}
                    onChange={handleFilterChange}
                    className="w-full mt-1 p-2 border border-gray-300"
                    placeholder={t("rentPage.filters.locationPlaceholder")}
                  />
                </div>
                <div>
                  <label className="block text-gray-700">{t("rentPage.filters.meters")}</label>
                  <input
                    type="number"
                    name="meters"
                    value={filterInputs.meters}
                    onChange={handleFilterChange}
                    className="w-full mt-1 p-2 border border-gray-300"
                    placeholder={t("rentPage.filters.metersPlaceholder")}
                  />
                </div>
                <button
                  onClick={clearFilters}
                  className="w-full bg-primary text-white p-2 hover:bg-secondary mt-4"
                >
                  {t("rentPage.filters.clearFilters")}
                </button>
              </div>
            </div>

            {/* Houses Grid Section */}
            <div className="w-full">
              {loading ? (
                <div className="text-left p-4 bg-white rounded-md">
                  <p>{t("salePage.loading")}</p>
                </div>
              ) : error ? (
                <div className="text-left p-4 bg-white rounded-md">
                  <p>
                    {t("salePage.error")}: {error}
                  </p>
                </div>
              ) : houses.length === 0 ? (
                <div className="text-left p-4 bg-white rounded-md">
                  <p>{t("rentPage.noHouses")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {houses.map((house) => (
                    <Link
                      to={`/prona/${house.id}`}
                      key={house.id}
                      onClick={() => handlePropertyClick(house)}
                      className="bg-white shadow-xl rounded-md"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={house.fields["Pictures"]?.[0]?.url || NoImage}
                        alt={house.fields["Title"]}
                        className="w-full h-40 object-cover rounded-t-md"
                      />
                      <div className="p-4">
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          <h3 className="text-lg font-bold">
                            {house.fields["Title"] || t("propertiesPage.noTitle")}
                          </h3>
                          <p className="text-white bg-secondary w-fit h-fit rounded-md px-2 py-0.5 justify-self-end">
                            {house.fields["Updated price"]
                              ? `${Number(house.fields["Updated price"]).toLocaleString()} `
                              : house.fields["Starter price"]
                              ? `${Number(house.fields["Starter price"]).toLocaleString()} `
                              : "- "}
                            {house.fields["Currency"]}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-gray-700 text-sm">
                          <div className="flex items-center">
                            <FaHouse className="mr-1" /> {house.fields["Type"] || "-"}
                          </div>
                          <div className="flex items-center">
                            <FaCircleInfo className="mr-1" />
                            {currentLanguage === "en"
                              ? house.fields["EN Status"] || t("propertiesPage.noStatus")
                              : house.fields["AL Status"] || t("propertiesPage.noStatus")}
                          </div>
                          <div className="flex items-center">
                            <FaBed className="mr-1" /> {house.fields["Bedrooms"] || "-"} {t("rentPage.bedrooms")}
                          </div>
                          <div className="flex items-center">
                            <FaBath className="mr-1" /> {house.fields["Baths"] || "-"} {t("rentPage.bathrooms")}
                          </div>
                          <div className="flex items-center">
                            <FaMapLocationDot className="mr-1" /> {house.fields["City"] || "-"}
                          </div>
                          <div className="flex items-center">
                            <FaRulerCombined className="mr-1" /> {house.fields["Land m2"] || "-"} m<sup>2</sup>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  disabled={!prevOffsets.length}
                  className="px-4 py-2 bg-gray-200 disabled:opacity-50"
                >
                  {t("rentPage.back")}
                </button>
                <button
                  onClick={handleNext}
                  disabled={!nextOffset}
                  className="px-4 py-2 bg-gray-200 disabled:opacity-50"
                >
                  {t("rentPage.next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalePage;
